import { theme } from "@shared/utils/themeColors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../shared/contexts/ToastContext";
import { SubscriptionService } from "../services/subscription.service";
import {
  ApplyCouponRequest,
  Coupon,
  CouponDiscountType,
} from "../types/subscription.types";

interface PlansPanelProps {
  user?: any;
}

function PlansPanel({ user }: PlansPanelProps) {
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch subscription plans
  const {
    data: plans = [],
    isLoading: isLoadingPlans,
    error: plansError,
  } = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: () => SubscriptionService.getPlans(),
  });

  // Fetch current subscription
  const {
    data: currentSubscription,
    isLoading: isLoadingSubscription,
    error: subscriptionError,
  } = useQuery({
    queryKey: ["currentSubscription"],
    queryFn: () => SubscriptionService.getCurrentSubscription(),
  });

  // Apply coupon mutation
  const { mutate: applyCoupon, isPending: isApplyingCoupon } = useMutation({
    mutationFn: (request: ApplyCouponRequest) =>
      SubscriptionService.applyCoupon(request),
    onSuccess: (data) => {
      if (data.isValid && data.coupon) {
        setAppliedCoupon(data.coupon);

        // Check if it's a time-limited access coupon
        if (
          data.coupon.discountType === CouponDiscountType.TIME_LIMITED_ACCESS
        ) {
          // Show a different message for time-limited access coupons
          const durationText =
            data.coupon.accessDurationMonths === 1
              ? "1 month"
              : `${data.coupon.accessDurationMonths} months`;
          showToast(
            `Time-limited access coupon applied: ${durationText} of premium access`,
            "success"
          );
        } else {
          // Create confirmation message based on applicable plans for regular discount coupons
          let message = `Coupon applied: ${data.coupon.discountValue} ${
            data.coupon.discountType === CouponDiscountType.FIXED_AMOUNT
              ? ""
              : "%"
          } discount`;

          // If the coupon only applies to specific plans
          if (
            data.coupon.applicablePlanIds &&
            data.coupon.applicablePlanIds.length > 0
          ) {
            const planNames = data.coupon.applicablePlanIds
              .map((id) => {
                return id === "monthly"
                  ? "Monthly"
                  : id === "yearly"
                  ? "yearly"
                  : id;
              })
              .join(", ");
            message += ` (only for plan${
              data.coupon.applicablePlanIds.length > 1 ? "s" : ""
            }: ${planNames})`;
          }

          showToast(message, "success");
        }
      } else {
        showToast("Invalid or expired coupon", "error");
      }
    },
    onError: (error) => {
      console.error(error);
      showToast(error.message || "Error applying coupon", "error");
    },
  });

  // Apply time-limited access coupon mutation
  const {
    mutate: applyTimeLimitedAccessCoupon,
    isPending: isApplyingTimeLimitedCoupon,
  } = useMutation({
    mutationFn: (couponCode: string) =>
      SubscriptionService.applyTimeLimitedAccessCoupon(couponCode),
    onSuccess: (data) => {
      if (data.success) {
        showToast(
          data.message || "Time-limited access coupon applied successfully",
          "success"
        );
        queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
        setCouponCode("");
        setShowCouponInput(false);
        setAppliedCoupon(null);
      } else {
        showToast(
          data.message || "Failed to apply time-limited access coupon",
          "error"
        );
      }
    },
    onError: (error: any) => {
      console.error(error);
      showToast(
        error.message || "Error applying time-limited access coupon",
        "error"
      );
    },
  });

  // Change plan mutation
  const { mutate: changePlan, isPending: isChangingPlan } = useMutation({
    mutationFn: (planId: string) =>
      SubscriptionService.changePlan({
        planId,
        couponCode: appliedCoupon?.code,
      }),
    onSuccess: (data) => {
      if (data.success) {
        showToast(data.message, "success");
        queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });

        // If there's a redirect URL for payment, navigate to it
        if (data.redirectUrl) {
          window.location.href = data.redirectUrl;
        }
      } else {
        showToast(data.message, "error");
      }
    },
    onError: () => {
      showToast("Error changing subscription plan", "error");
    },
  });

  // Toggle auto-renewal mutation
  const { mutate: toggleAutoRenewal, isPending: isTogglingRenewal } =
    useMutation({
      mutationFn: (enable: boolean) =>
        enable
          ? SubscriptionService.enableAutoRenewal()
          : SubscriptionService.cancelAutoRenewal(),
      onSuccess: (data) => {
        showToast(data.message, "success");
        queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
      },
      onError: () => {
        showToast("Error updating auto-renewal settings", "error");
      },
    });

  // Cancel subscription at period end mutation
  const { mutate: cancelAtPeriodEnd, isPending: isCancelingAtPeriodEnd } =
    useMutation({
      mutationFn: (subscriptionId: string) =>
        SubscriptionService.cancelAtPeriodEnd(subscriptionId),
      onSuccess: (data) => {
        if (data.success) {
          showToast(
            data.message ||
              "Subscription will be canceled at the end of the current billing period",
            "success"
          );
          queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
        } else {
          showToast(data.message || "Failed to cancel subscription", "error");
        }
      },
      onError: (error: any) => {
        showToast(error.message || "Error canceling subscription", "error");
      },
    });

  // Downgrade to free plan mutation
  const { mutate: downgradeToFree, isPending: isDowngradingToFree } =
    useMutation({
      mutationFn: (subscriptionId: string) =>
        SubscriptionService.downgradeToFree(subscriptionId),
      onSuccess: (data) => {
        if (data.success) {
          showToast(
            data.message || "Successfully downgraded to free plan",
            "success"
          );
          queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
        } else {
          showToast(
            data.message || "Failed to downgrade to free plan",
            "error"
          );
        }
      },
      onError: (error: any) => {
        showToast(error.message || "Error downgrading to free plan", "error");
      },
    });

  // Downgrade yearly to monthly mutation
  const {
    mutate: downgradeYearlyToMonthly,
    isPending: isDowngradingToMonthly,
  } = useMutation({
    mutationFn: (subscriptionId: string) =>
      SubscriptionService.downgradeYearlyToMonthly(subscriptionId),
    onSuccess: (data) => {
      if (data.success) {
        showToast(
          data.message || "Successfully downgraded to monthly plan",
          "success"
        );
        queryClient.invalidateQueries({ queryKey: ["currentSubscription"] });
      } else {
        showToast(
          data.message || "Failed to downgrade to monthly plan",
          "error"
        );
      }
    },
    onError: (error: any) => {
      showToast(error.message || "Error downgrading to monthly plan", "error");
    },
  });

  const handleUpgradeMonthlyToYearly = async () => {
    if (!currentSubscription) {
      showToast("No active subscription found", "error");
      return;
    }

    if (currentSubscription?.planDetails?.pricing?.interval !== "month") {
      showToast(
        "This action is only available for monthly subscriptions",
        "error"
      );
      return;
    }

    try {
      setIsProcessing(true);
      // Call the API to get the checkout session
      const response = await SubscriptionService.upgradeMonthlyToYearly(
        currentSubscription.id
      );

      // If we have a session URL, redirect to it
      if (response.sessionUrl) {
        window.location.href = response.sessionUrl;
      } else {
        // Fallback to the confirmation page if no session URL is provided
        navigate(
          `/settings/subscription-upgrade-yearly-confirm/${currentSubscription.id}`
        );
      }
    } catch (error: any) {
      console.error("Error upgrading subscription:", error);
      showToast(
        error.message || "Failed to upgrade subscription. Please try again.",
        "error"
      );
    } finally {
      setIsProcessing(false);
    }
  };

  // Check if coupon is applicable to a plan
  const isCouponApplicableToPlan = (planId: string) => {
    if (!appliedCoupon) return false;

    // If applicablePlanIds is empty or undefined, the coupon applies to all plans
    if (
      !appliedCoupon.applicablePlanIds ||
      appliedCoupon.applicablePlanIds.length === 0
    ) {
      return true;
    }

    // Check if the plan ID is in the applicable plans list
    return appliedCoupon.applicablePlanIds.includes(planId);
  };

  // Calculate discounted price
  const getDiscountedPrice = (price: number, planId: string) => {
    if (!appliedCoupon || !isCouponApplicableToPlan(planId)) return price;

    if (appliedCoupon.discountType === CouponDiscountType.PERCENTAGE) {
      const discountAmount = (price * appliedCoupon.discountValue) / 100;
      return Math.max(0, price - discountAmount).toFixed(2);
    }

    if (appliedCoupon.discountType === CouponDiscountType.FIXED_AMOUNT) {
      return Math.max(0, price - appliedCoupon.discountValue).toFixed(2);
    }

    return price;
  };

  // Handle coupon submission
  const handleApplyCoupon = () => {
    if (!couponCode.trim()) {
      showToast("Please enter a coupon code", "error");
      return;
    }

    applyCoupon({ code: couponCode.trim() });
  };

  // Handle time-limited access coupon application
  const handleApplyTimeLimitedAccessCoupon = () => {
    if (!appliedCoupon) {
      showToast("Please apply a valid coupon first", "error");
      return;
    }

    if (appliedCoupon.discountType !== CouponDiscountType.TIME_LIMITED_ACCESS) {
      showToast("This coupon is not valid for time-limited access", "error");
      return;
    }

    applyTimeLimitedAccessCoupon(appliedCoupon.code);
  };

  // Handle plan selection
  const handleSelectPlan = async (planId: string) => {
    if (currentSubscription?.planId === planId) {
      return;
    }

    // If selecting a paid plan, redirect to checkout
    if (planId !== "free") {
      try {
        setIsCheckingOut(true);
        const { sessionUrl } = await SubscriptionService.createCheckoutSession(
          planId,
          appliedCoupon?.code
        );

        // Redirect to Stripe Checkout
        window.location.href = sessionUrl;
      } catch (error) {
        console.error("Error creating checkout session:", error);
        showToast(
          "Failed to create checkout session. Please try again.",
          "error"
        );
        setIsCheckingOut(false);
      }
      return;
    }

    // For free plan, change directly
    changePlan(planId);
  };

  // Handle cancel at period end
  const handleCancelAtPeriodEnd = () => {
    if (!currentSubscription?.stripeSubscriptionId) {
      showToast("No active subscription found", "error");
      return;
    }

    navigate(
      `/settings/subscription-cancel-confirm/${currentSubscription.stripeSubscriptionId}`
    );
  };

  const handleDowngradeToFree = () => {
    if (!currentSubscription) {
      showToast("No active subscription found", "error");
      return;
    }

    if (currentSubscription?.planDetails?.type === "FREE") {
      showToast("You are already on the free plan", "error");
      return;
    }

    // For plans with single payment (no stripeSubscriptionId)
    if (!currentSubscription.stripeSubscriptionId) {
      // Use subscription ID instead of stripeSubscriptionId
      navigate(
        `/settings/subscription-downgrade-free-confirm/${currentSubscription.id}`
      );
      return;
    }

    if (!currentSubscription.stripeSubscriptionId) {
      showToast("No active subscription found", "error");
      return;
    }

    navigate(
      `/settings/subscription-downgrade-free-confirm/${currentSubscription.stripeSubscriptionId}`
    );
  };

  // Handle downgrade yearly to monthly
  const handleDowngradeYearlyToMonthly = () => {
    if (!currentSubscription) {
      showToast("No active subscription found", "error");
      return;
    }

    if (currentSubscription?.planDetails?.pricing?.interval !== "year") {
      showToast(
        "This action is only available for yearly subscriptions",
        "error"
      );
      return;
    }

    // For yearly plans with single payment (no stripeSubscriptionId)
    if (
      !currentSubscription.stripeSubscriptionId &&
      currentSubscription.planDetails?.type === "YEARLY"
    ) {
      // Use subscription ID instead of stripeSubscriptionId
      navigate(
        `/settings/subscription-downgrade-monthly-confirm/${currentSubscription.id}`
      );
      return;
    }

    if (!currentSubscription.stripeSubscriptionId) {
      showToast("No active subscription found", "error");
      return;
    }

    navigate(
      `/settings/subscription-downgrade-monthly-confirm/${currentSubscription.stripeSubscriptionId}`
    );
  };

  // Format date for display
  const formatDate = (date: Date | undefined) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Show error messages if API calls fail
  useEffect(() => {
    if (plansError) {
      showToast("Error loading subscription plans.", "error");
    }
    if (subscriptionError) {
      showToast("Error loading your current subscription.", "error");
    }
  }, [plansError, subscriptionError, showToast]);

  // Check if current plan is yearly to show yearly to monthly option
  const isYearlyPlan =
    currentSubscription?.planDetails?.pricing?.interval === "year";

  // Check if current plan is monthly to show monthly to yearly option
  const isMonthlyPlan =
    currentSubscription?.planDetails?.pricing?.interval === "month";

  // Check if current plan is free to hide downgrade to free option
  const isFreeplan = currentSubscription?.planDetails?.type === "FREE";

  // Render subscription management section
  const renderSubscriptionManagement = () => {
    if (!currentSubscription) return null;

    // Check if subscription is already set to cancel at period end
    const cancelAtPeriodEndActive = currentSubscription.cancelAtPeriodEnd;

    const isPending =
      isCancelingAtPeriodEnd || isDowngradingToFree || isDowngradingToMonthly;

    return (
      <section>
        <div className="mt-6 border-t border-gray-200 dark:border-gray-700/60 pt-6">
          <h3 className={`text-lg font-semibold ${theme.text.primary} mb-4`}>
            Subscription Management
          </h3>

          <div className="space-y-4">
            {!cancelAtPeriodEndActive && !isFreeplan && !isYearlyPlan && (
              <button
                onClick={handleCancelAtPeriodEnd}
                disabled={isPending}
                className={`btn ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300 w-full`}
              >
                {isCancelingAtPeriodEnd
                  ? "Processing..."
                  : "Cancel at Period End"}
              </button>
            )}

            {cancelAtPeriodEndActive && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/30 rounded-md p-3 text-sm text-yellow-800 dark:text-yellow-200">
                Your subscription is set to cancel at the end of the current
                billing period.
              </div>
            )}

            {!isFreeplan && !isYearlyPlan && (
              <button
                onClick={handleDowngradeToFree}
                disabled={isPending}
                className={`btn ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300 w-full`}
              >
                {isDowngradingToFree
                  ? "Processing..."
                  : "Downgrade to Free Plan"}
              </button>
            )}

            {isYearlyPlan && !isFreeplan && !cancelAtPeriodEndActive && (
              <button
                onClick={handleDowngradeYearlyToMonthly}
                disabled={isPending}
                className={`btn ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300 w-full`}
              >
                {isDowngradingToMonthly
                  ? "Processing..."
                  : "Switch to Monthly Billing"}
              </button>
            )}

            {isMonthlyPlan && !isFreeplan && !cancelAtPeriodEndActive && (
              <button
                onClick={handleUpgradeMonthlyToYearly}
                disabled={isPending}
                className={`btn ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300 w-full`}
              >
                Switch to Yearly Billing
              </button>
            )}

            {/* <button
              onClick={() => navigate("/settings/subscription-history")}
              className={`btn ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300 w-full flex items-center justify-center`}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              View Subscription History
            </button> */}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="grow">
      {/* Panel body */}
      <div className="p-6 space-y-6">
        {/* Plans */}
        <section>
          <div className="mb-8">
            <h2 className={`text-2xl ${theme.text.primary} font-bold mb-4`}>
              Subscription Plans
            </h2>
            {currentSubscription && (
              <div className="text-sm">
                Your current plan is{" "}
                <strong className="font-medium">
                  {currentSubscription.planId}
                </strong>{" "}
                expire on{" "}
                {formatDate(currentSubscription.currentPeriodEnd.toDate())}
                {/* and will
                {currentSubscription.autoRenew
                  ? ` renew on ${formatDate(
                      currentSubscription.currentPeriodEnd.toDate()
                    )}.`
                  : ` expire on ${formatDate(
                      currentSubscription.currentPeriodEnd.toDate()
                    )}.`} */}
              </div>
            )}
          </div>

          {/* Pricing */}
          <div>
            {/* Pricing tabs */}
            <div className="grid grid-cols-12 gap-6">
              {plans &&
                plans?.map((plan, index) => {
                  const isCurrentPlan = currentSubscription?.planId === plan.id;
                  const bgColors = [
                    "bg-green-500",
                    "bg-sky-500",
                    "bg-violet-500",
                  ];
                  const bgColor = bgColors[index % bgColors.length];

                  return (
                    <div
                      key={plan.id}
                      className={`relative col-span-full xl:col-span-4 bg-white dark:bg-gray-800 border ${
                        plan.isPopular
                          ? "border-violet-200 dark:border-violet-900/30 shadow-md"
                          : "${theme.border.primary} shadow-sm"
                      } rounded-b-lg ${
                        plan.isPopular ? "ring-2 ring-violet-500" : ""
                      }`}
                    >
                      {plan.isPopular && (
                        <div className="absolute top-0 right-0 mr-6 -mt-4 bg-violet-600 text-white text-xs font-semibold py-1 px-3 rounded-full">
                          Most Popular
                        </div>
                      )}
                      <div
                        className={`absolute top-0 left-0 right-0 h-0.5 ${bgColor}`}
                        aria-hidden="true"
                      ></div>
                      <div
                        className={`px-5 pt-5 pb-6 border-b ${theme.border.primary}`}
                      >
                        <header className="flex items-center mb-2">
                          <div
                            className={`w-6 h-6 rounded-full shrink-0 ${bgColor} mr-3`}
                          >
                            <svg
                              className="w-6 h-6 fill-current text-white"
                              viewBox="0 0 24 24"
                            >
                              <path d="M12 17a.833.833 0 01-.833-.833 3.333 3.333 0 00-3.334-3.334.833.833 0 110-1.666 3.333 3.333 0 003.334-3.334.833.833 0 111.666 0 3.333 3.333 0 003.334 3.334c0 .46-.373.833-.833.833z" />
                            </svg>
                          </div>
                          <h3
                            className={`text-lg ${theme.text.primary} font-semibold`}
                          >
                            {plan.name}
                          </h3>
                        </header>
                        <div className="text-sm mb-2">{plan.description}</div>
                        {/* Price */}
                        <div className={`${theme.text.primary} font-bold mb-4`}>
                          {plan.id === "free" ||
                          appliedCoupon?.discountType ===
                            CouponDiscountType.TIME_LIMITED_ACCESS ? (
                            <span className="text-3xl">Free</span>
                          ) : (
                            <>
                              <span className="text-2xl">$</span>
                              <span className="text-3xl">
                                {appliedCoupon
                                  ? getDiscountedPrice(
                                      plan.pricing.amount,
                                      plan.id
                                    )
                                  : plan.pricing.amount}
                              </span>
                              <span className={theme.text.muted}>
                                {plan.id === "monthly" ? "/mo" : "/year"}
                              </span>
                              {appliedCoupon &&
                                isCouponApplicableToPlan(plan.id) && (
                                  <span className="ml-2 line-through text-gray-400 text-sm">
                                    ${plan.pricing.amount}
                                  </span>
                                )}
                            </>
                          )}
                        </div>
                        {/* CTA */}
                        {appliedCoupon &&
                        appliedCoupon.discountType ===
                          CouponDiscountType.TIME_LIMITED_ACCESS &&
                        plan.id === "monthly" ? (
                          <button
                            className="btn w-full bg-green-600 text-white hover:bg-green-700 dark:bg-green-600 dark:text-white dark:hover:bg-green-700"
                            onClick={handleApplyTimeLimitedAccessCoupon}
                            disabled={isApplyingTimeLimitedCoupon}
                          >
                            {isApplyingTimeLimitedCoupon
                              ? "Processing..."
                              : `Activate ${
                                  appliedCoupon.accessDurationMonths
                                } Month${
                                  appliedCoupon.accessDurationMonths !== 1
                                    ? "s"
                                    : ""
                                } Access`}
                          </button>
                        ) : isCurrentPlan ? (
                          <button
                            className="btn w-full bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white disabled:border-gray-200 dark:disabled:border-gray-700 disabled:bg-white dark:disabled:bg-gray-800 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed"
                            disabled
                          >
                            <svg
                              className="w-3 h-3 shrink-0 fill-current mr-2"
                              viewBox="0 0 12 12"
                            >
                              <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                            </svg>
                            <span>Current Plan</span>
                          </button>
                        ) : (
                          <button
                            className={`btn w-full ${
                              index === 0
                                ? "${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300"
                                : "bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white"
                            } ${
                              (isYearlyPlan && plan.id === "monthly") ||
                              plan.id === "free"
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                            onClick={() =>
                              isMonthlyPlan && plan.id === "yearly"
                                ? handleUpgradeMonthlyToYearly()
                                : handleSelectPlan(plan.id)
                            }
                            disabled={
                              isCheckingOut ||
                              (isProcessing &&
                                isMonthlyPlan &&
                                plan.id === "yearly") ||
                              (isYearlyPlan && plan.id === "monthly") ||
                              plan.id === "free"
                            }
                          >
                            {isCheckingOut ||
                            (isProcessing &&
                              isMonthlyPlan &&
                              plan.id === "yearly")
                              ? "Processing..."
                              : plan.id === "free"
                              ? "Downgrade"
                              : plan.id === currentSubscription?.planId
                              ? "Current Plan"
                              : isMonthlyPlan && plan.id === "yearly"
                              ? "Upgrade to Yearly"
                              : isYearlyPlan && plan.id === "monthly"
                              ? "Contact Support"
                              : "Upgrade"}
                          </button>
                        )}
                      </div>
                      <div className="px-5 pt-4 pb-5">
                        <div
                          className={`text-xs ${theme.text.primary} font-semibold uppercase mb-4`}
                        >
                          What's included
                        </div>
                        {/* List */}
                        <ul>
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-center py-1">
                              <svg
                                className={`w-3 h-3 shrink-0 fill-current ${theme.text.success} mr-2`}
                                viewBox="0 0 12 12"
                              >
                                <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                              </svg>
                              <div className="text-sm">{feature}</div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>

        {/* Coupon section */}
        <section>
          <div className="px-5 py-3 bg-[linear-gradient(135deg,var(--tw-gradient-stops))] from-violet-500/[0.12] dark:from-violet-500/[0.24] to-violet-500/[0.04] rounded-lg text-center xl:text-left xl:flex xl:flex-wrap xl:justify-between xl:items-center">
            {showCouponInput ? (
              <div className="w-full xl:w-auto flex flex-col xl:flex-row items-center space-y-2 xl:space-y-0 xl:space-x-2">
                <input
                  type="text"
                  className="form-input w-full xl:w-64"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value)}
                />
                <button
                  className="btn bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white"
                  onClick={handleApplyCoupon}
                  disabled={isApplyingCoupon || isApplyingTimeLimitedCoupon}
                >
                  {isApplyingCoupon ? "Applying..." : "Apply Coupon"}
                </button>
                <button
                  className={`btn ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300`}
                  onClick={() => {
                    setShowCouponInput(false);
                    setCouponCode("");
                    setAppliedCoupon(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <>
                <div
                  className={`${theme.text.primary} font-semibold mb-2 xl:mb-0`}
                >
                  {appliedCoupon
                    ? `Coupon applied: ${appliedCoupon.code} (${
                        appliedCoupon.discountValue
                      } ${
                        appliedCoupon.discountType ===
                        CouponDiscountType.FIXED_AMOUNT
                          ? ""
                          : "%"
                      } discount${
                        appliedCoupon.applicablePlanIds &&
                        appliedCoupon.applicablePlanIds.length > 0
                          ? `, only for ${appliedCoupon.applicablePlanIds
                              .map((id) =>
                                id === "monthly"
                                  ? "Monthly"
                                  : id === "yearly"
                                  ? "yearly"
                                  : id
                              )
                              .join(", ")}`
                          : ""
                      })`
                    : "Do you have a coupon code?"}
                </div>
                <button
                  className="btn bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white"
                  onClick={() => setShowCouponInput(true)}
                >
                  {appliedCoupon ? "Change Coupon" : "Apply Coupon"}
                </button>
              </>
            )}
          </div>
        </section>

        {/* Subscription Management Section */}
        {renderSubscriptionManagement()}

        {/* FAQs */}
        <section>
          <div className="my-8">
            <h2 className={`text-2xl ${theme.text.primary} font-bold`}>FAQs</h2>
          </div>
          <ul className="space-y-5">
            <li>
              <div className={`font-semibold ${theme.text.primary} mb-1`}>
                What is the difference between the subscription plans?
              </div>
              <div className="text-sm">
                Each plan offers different levels of access to our test
                preparation platform. The Free plan provides basic access with
                limited features, the Monthly plan offers full access with a
                monthly payment, and the Annual plan gives you the same features
                at a discounted rate with yearly billing.
              </div>
            </li>
            <li>
              <div className={`font-semibold ${theme.text.primary} mb-1`}>
                Can I change my subscription plan?
              </div>
              <div className="text-sm">
                Yes, you can upgrade or downgrade your subscription at any time.
                When upgrading, you'll be charged the prorated difference for
                the remainder of your billing cycle. When downgrading, the new
                plan will take effect at the start of your next billing cycle.
              </div>
            </li>
            <li>
              <div className={`font-semibold ${theme.text.primary} mb-1`}>
                Got more questions?
              </div>
              <div className="text-sm">
                If you have any other questions about our subscription plans or
                need assistance, please{" "}
                <a
                  className={`font-medium ${theme.text.brand} hover:text-violet-600 dark:hover:text-violet-400`}
                  href="mailto:info@preppinbee.com"
                >
                  contact our support team
                </a>
                .
              </div>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
}

export default PlansPanel;
